import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { ProgressBar } from 'ui-components/components/progress-bar';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import styles from './styles.module.less';
import messages from './user-count-display-messages';
import { useGlobalModalLaunchContext } from '../../../../contexts/modal-context/global-modal-context';
import Global from '../../../../store/global-selectors';
import { useUserLimitData } from '../../payment-and-subscription/hooks/use-plan-details';
import { SectionIds } from '../../payment-and-subscription/components/manage-subscription-drawer/constants';

export const UserCountDisplay = () => {
  const planName = useSelector(Global.selectSellerPlanName);
  const users = useSelector(Global.selectSellerUsers);
  const activeUserCount = users.filter((u: { isActive: boolean }) => u.isActive).length;
  const { totalUserCount, hasUnlimitedUsers } = useUserLimitData();
  const globalModalContext = useGlobalModalLaunchContext();

  const handleAddUsers = useCallback(() => {
    globalModalContext?.openManageSubscriptionDrawer({ highlightSection: SectionIds.Users });
  }, [globalModalContext]);

  const percentageComplete = hasUnlimitedUsers ? 0 : Math.min(100, Math.round((activeUserCount / totalUserCount) * 100));

  return (
    <FlexColumn className={styles.card} gap={Gap.Space4}>
      <FlexRow className={styles.header}>
        <Text isBold textType={TextType.H4} value={planName} />
        <Link className={styles['plan-details-link']} to='/settings/payments'>
          <Text textType={TextType.Caption} value={messages.planDetails} />
          <FontAwesomeIcon icon={faArrowRight} size='xs' />
        </Link>
      </FlexRow>
      <Text textType={TextType.Label} value={messages.totalUsers} />
      <ProgressBar
        className={styles['progress-bar']}
        percentageComplete={percentageComplete}
        wrapperClassName={styles['progress-bar-wrapper']}
      />
      <FlexRow className={styles['count-row']} gap={Gap.Space4}>
        <Text
          className={styles.count}
          textType={TextType.H3}
          value={hasUnlimitedUsers ? `${activeUserCount}` : `${activeUserCount}/${totalUserCount}`}
        />
        <Text textType={TextType.Paragraph} value={messages.activeUsers} />
        {!hasUnlimitedUsers && (
          <Button className={styles['count-row-button']} onClick={handleAddUsers} theme={THEME_CONSTANTS.tertiary}>
            <FontAwesomeIcon icon={faPlus} size='xs' />
            <Text textType={TextType.Caption} value={messages.addUsers} />
          </Button>
        )}
      </FlexRow>
    </FlexColumn>
  );
};
